import { useState, useEffect } from 'react';
import api from '../services/api';
import { FloppyDisk, Buildings, Bank } from '@phosphor-icons/react';

export default function Settings() {
  const [form, setForm] = useState({
    briefkopf_name: '',
    briefkopf_strasse: '',
    briefkopf_plz: '',
    briefkopf_ort: '',
    briefkopf_telefon: '',
    briefkopf_email: '',
    briefkopf_iban: '',
    briefkopf_bic: '',
    briefkopf_bank: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get('/settings/briefkopf').then(res => {
      setForm(prev => ({ ...prev, ...Object.fromEntries(Object.entries(res.data || {}).map(([k, v]) => [k, v || ''])) }));
    }).finally(() => setLoading(false));
  }, []);

  const update = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    try {
      await api.put('/settings/briefkopf', form);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      alert('Speichern fehlgeschlagen: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-slate-900">Einstellungen</h1>
        <p className="text-slate-500 mt-1 text-sm">Briefkopf-Daten für Jahresabrechnungen & Mahnungen</p>
      </div>

      <form onSubmit={save} className="space-y-6">
        <div className="bg-white border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
            <Buildings weight="bold" className="w-5 h-5 text-slate-700" />
            <h2 className="font-bold text-slate-900">Absender / Hausverwaltung</h2>
          </div>
          <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Name / Firma" value={form.briefkopf_name} onChange={update('briefkopf_name')} placeholder="z.B. Mustermann Hausverwaltung" testid="briefkopf-name" colSpan="sm:col-span-2" />
            <Field label="Straße + Hausnummer" value={form.briefkopf_strasse} onChange={update('briefkopf_strasse')} placeholder="Musterstraße 12" testid="briefkopf-strasse" colSpan="sm:col-span-2" />
            <Field label="PLZ" value={form.briefkopf_plz} onChange={update('briefkopf_plz')} placeholder="50667" testid="briefkopf-plz" />
            <Field label="Ort" value={form.briefkopf_ort} onChange={update('briefkopf_ort')} placeholder="Köln" testid="briefkopf-ort" />
            <Field label="Telefon" value={form.briefkopf_telefon} onChange={update('briefkopf_telefon')} placeholder="+49 ..." testid="briefkopf-telefon" />
            <Field label="E-Mail" value={form.briefkopf_email} onChange={update('briefkopf_email')} placeholder="info@..." testid="briefkopf-email" />
          </div>
        </div>

        <div className="bg-white border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
            <Bank weight="bold" className="w-5 h-5 text-slate-700" />
            <h2 className="font-bold text-slate-900">Bankverbindung (für Nachzahlungen)</h2>
          </div>
          <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="IBAN" value={form.briefkopf_iban} onChange={update('briefkopf_iban')} placeholder="DE89 3704 0044 0532 0130 00" testid="briefkopf-iban" colSpan="sm:col-span-2" />
            <Field label="BIC" value={form.briefkopf_bic} onChange={update('briefkopf_bic')} placeholder="COBADEFFXXX" testid="briefkopf-bic" />
            <Field label="Bank" value={form.briefkopf_bank} onChange={update('briefkopf_bank')} placeholder="z.B. Commerzbank Köln" testid="briefkopf-bank" />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            className="bg-slate-900 text-white px-6 py-2.5 font-medium hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2"
            data-testid="briefkopf-save"
          >
            <FloppyDisk weight="bold" className="w-4 h-4" />
            {saving ? 'Speichere...' : 'Briefkopf speichern'}
          </button>
          {saved && <span className="text-emerald-600 text-sm font-medium" data-testid="briefkopf-saved-confirmation">Gespeichert ✓</span>}
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid, colSpan = '' }) {
  return (
    <div className={colSpan}>
      <label className="block text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full border-b border-slate-300 bg-transparent py-2 focus:border-slate-900 outline-none"
        data-testid={testid}
      />
    </div>
  );
}
